/**
 * useForgeSSE Hook
 *
 * Manages Server-Sent Events (SSE) connection for real-time execution progress
 * Implements automatic reconnection with exponential backoff
 * Handles heartbeat detection and connection status
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { forgeApi } from '../services/forgeApi';
import type {
  ExecutionStatus,
  ExecutionPhase,
  PhaseStatus,
  ExecutionPhaseData,
  SSEEvent,
  UseForgeSSEResult,
} from '../types/forge';

const MAX_RECONNECT_ATTEMPTS = 5;
const INITIAL_RECONNECT_DELAY = 1000; // 1 second
const MAX_RECONNECT_DELAY = 30000; // 30 seconds

/**
 * Hook for managing SSE connection to execution progress stream
 */
export const useForgeSSE = (): UseForgeSSEResult => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [executionStatus, setExecutionStatus] = useState<ExecutionStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const requirementIdRef = useRef<number | null>(null);

  /**
   * Calculate reconnection delay with exponential backoff
   */
  const getReconnectDelay = (): number => {
    const delay = Math.min(
      INITIAL_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current),
      MAX_RECONNECT_DELAY
    );
    return delay;
  };

  /**
   * Initialize execution status with pending phases
   */
  const initializeExecutionStatus = (requirementId: number): ExecutionStatus => {
    const phases: ExecutionPhaseData[] = [
      { phase: 'analyzing', status: 'pending' },
      { phase: 'planning', status: 'pending' },
      { phase: 'implementing', status: 'pending' },
      { phase: 'testing', status: 'pending' },
      { phase: 'creating_pr', status: 'pending' },
    ];

    return {
      requirementId,
      status: 'in_progress',
      currentPhase: 'analyzing',
      phases,
      updatedAt: new Date().toISOString(),
    };
  };

  /**
   * Update phase status in execution status
   */
  const updatePhaseStatus = (
    status: ExecutionStatus,
    phase: ExecutionPhase,
    phaseStatus: PhaseStatus
  ): ExecutionStatus => {
    const updatedPhases = status.phases.map((p) => {
      if (p.phase === phase) {
        return {
          ...p,
          status: phaseStatus,
          startedAt: phaseStatus === 'in_progress' ? new Date().toISOString() : p.startedAt,
          completedAt: phaseStatus === 'completed' ? new Date().toISOString() : p.completedAt,
        };
      }
      return p;
    });

    return {
      ...status,
      currentPhase: phase,
      phases: updatedPhases,
      updatedAt: new Date().toISOString(),
    };
  };

  /**
   * Handle SSE message events
   */
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const data: SSEEvent = JSON.parse(event.data);

      if (data.type === 'heartbeat') {
        // Heartbeat received, connection is alive
        return;
      }

      setExecutionStatus((prevStatus) => {
        if (!prevStatus) return prevStatus;

        switch (data.type) {
          case 'status_update':
            if (data.data.status) {
              return {
                ...prevStatus,
                status: data.data.status,
                updatedAt: data.timestamp,
              };
            }
            return prevStatus;

          case 'phase_change':
            if (data.data.phase && data.data.phaseStatus) {
              return updatePhaseStatus(prevStatus, data.data.phase, data.data.phaseStatus);
            }
            return prevStatus;

          case 'execution_complete':
            return {
              ...prevStatus,
              status: 'completed',
              prUrl: data.data.prUrl,
              updatedAt: data.timestamp,
            };

          case 'error':
            return {
              ...prevStatus,
              status: 'failed',
              error: data.data.error || data.data.message,
              updatedAt: data.timestamp,
            };

          default:
            return prevStatus;
        }
      });
    } catch (err) {
      console.error('Failed to parse SSE message:', err);
    }
  }, []);

  /**
   * Handle SSE connection open
   */
  const handleOpen = useCallback(() => {
    setIsConnected(true);
    setError(null);
    reconnectAttemptsRef.current = 0;
  }, []);

  /**
   * Handle SSE connection error
   */
  const handleError = useCallback(() => {
    setIsConnected(false);
    setError('Connection lost. Attempting to reconnect...');

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    // Attempt reconnection
    if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS && requirementIdRef.current) {
      const delay = getReconnectDelay();
      reconnectAttemptsRef.current += 1;

      reconnectTimeoutRef.current = setTimeout(() => {
        if (requirementIdRef.current) {
          connect(requirementIdRef.current);
        }
      }, delay) as unknown as number;
    } else {
      setError('Failed to connect. Maximum reconnection attempts reached.');
    }
  }, []);

  /**
   * Connect to SSE stream for a requirement
   */
  const connect = useCallback(
    (requirementId: number) => {
      // Close existing connection
      disconnect();

      // Store requirement ID for reconnection
      requirementIdRef.current = requirementId;

      // Initialize execution status
      setExecutionStatus(initializeExecutionStatus(requirementId));

      // Create new EventSource
      const streamUrl = forgeApi.execution.getStreamUrl(requirementId);
      const eventSource = new EventSource(streamUrl);

      eventSource.addEventListener('open', handleOpen);
      eventSource.addEventListener('message', handleMessage);
      eventSource.addEventListener('error', handleError);

      eventSourceRef.current = eventSource;
    },
    [handleOpen, handleMessage, handleError]
  );

  /**
   * Disconnect from SSE stream
   */
  const disconnect = useCallback(() => {
    // Clear reconnection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close EventSource connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setIsConnected(false);
    requirementIdRef.current = null;
    reconnectAttemptsRef.current = 0;
  }, []);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    executionStatus,
    connect,
    disconnect,
    error,
  };
};

export default useForgeSSE;
